import { lazy, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

// project imports
import Loadable from 'components/Loadable';
import { checkAuth } from 'utils/auth';
import CustomDomainRoute from 'routes/CustomDomainRoute';

const LandingPage = Loadable(lazy(() => import('pages/LandingPage')));

// Hosts that always get the normal admin flow
const MAIN_HOSTS = new Set([
  'zoidesq.com',
  'www.zoidesq.com',
  'appointments.zoiteckh.com',
  'localhost',
  '127.0.0.1',
]);

// ==============================|| PROTECTED ROUTE ||============================== //

/**
 * Protected Route component.
 *
 * Root path ("/"):
 *   - On a main host (appointments.zoiteckh.com) → always show the public
 *     landing page. Admins reach the dashboard via /dashboard after login.
 *   - On a custom tenant domain → resolve the domain to a company slug and
 *     render that tenant's booking page.
 *
 * Non-root paths → usual auth check, redirects to /login if not logged in.
 */
export default function ProtectedRoute({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const host = typeof window !== 'undefined' ? window.location.hostname : '';
  const isCustomHost = host && !MAIN_HOSTS.has(host);
  const isRoot = location.pathname === '/' || location.pathname === '';

  useEffect(() => {
    // The root path is public (landing page / tenant booking page) — only
    // protected (non-root) dashboard paths run the auth check.
    if (!isRoot) {
      checkAuth(navigate);
    }
  }, [navigate, isRoot]);

  if (isRoot) {
    return isCustomHost ? <CustomDomainRoute fallback={children} /> : <LandingPage />;
  }

  return children;
}
